import { Component } from '@theme/component';
import { StandardEvents } from '@shopify/events';
import { DrawerOpenEvent } from '@theme/theme-drawer';

/**
 * Cart Drawer Component
 *
 * Handles:
 * - Automatic opening of the cart drawer after Add to Cart
 * - AJAX Add to Cart for the custom product information form
 * - Refreshing the cart drawer using Shopify Section Rendering API
 * - Sticky cart summary behavior
 * - Installments CTA behavior
 */
class CartDrawerComponent extends Component {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */
  get #themeDrawer() {
    return /** @type {import('@theme/theme-drawer').ThemeDrawer | null} */ (
      this.closest('theme-drawer')
    );
  }

  /** @type {HTMLDialogElement | null} */
  get #dialog() {
    return this.closest('dialog');
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(
      StandardEvents.cartLinesUpdate,
      this.#handleCartLinesUpdate
    );

    this.#themeDrawer?.addEventListener(
      DrawerOpenEvent.eventName,
      this.#handleDrawerOpen
    );

    /*
     * Handle custom product form submissions.
     *
     * The custom product-information.liquid form is a normal Shopify
     * product form, so without this interception Shopify redirects
     * directly to /cart.
     */
    document.addEventListener('submit', this.#handleProductFormSubmit);

    /*
     * The restore path may set [open] before this module loads.
     */
    if (this.#themeDrawer?.hasAttribute('open')) {
      this.#handleDrawerOpen();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(
      StandardEvents.cartLinesUpdate,
      this.#handleCartLinesUpdate
    );

    this.#themeDrawer?.removeEventListener(
      DrawerOpenEvent.eventName,
      this.#handleDrawerOpen
    );

    document.removeEventListener(
      'submit',
      this.#handleProductFormSubmit
    );
  }

  /**
   * Handles Add to Cart from the custom product information form.
   *
   * @param {SubmitEvent} event
   */
  #handleProductFormSubmit = async (event) => {
    const form = event.target;

    if (!(form instanceof HTMLFormElement)) return;

    /*
     * Only handle the custom product form created in
     * product-information.liquid.
     */
    if (form.id !== 'CustomProductForm') return;

    /*
     * Do not intercept Buy It Now.
     *
     * Buy It Now adds a return_to=/checkout input and should continue
     * with its normal checkout behavior.
     */
    if (form.querySelector('input[name="return_to"]')) return;

    /*
     * Only intercept an actual Add to Cart submission.
     */
    const submitter = event.submitter;

    if (
      submitter &&
      submitter instanceof HTMLButtonElement &&
      submitter.id !== 'AddToCart'
    ) {
      return;
    }

    event.preventDefault();

    const addButton = form.querySelector('#AddToCart');
    const addButtonText = form.querySelector('#AddToCartText');

    if (addButton?.disabled) return;

    /*
     * Prevent duplicate clicks while the AJAX request is running.
     */
    if (form.dataset.cartSubmitting === 'true') return;

    form.dataset.cartSubmitting = 'true';

    const originalButtonText = addButtonText?.textContent || 'Add to Cart';

    if (addButton) {
      addButton.disabled = true;
    }

    if (addButtonText) {
      addButtonText.textContent = 'Adding...';
    }

    try {
      const formData = new FormData(form);

      /*
       * Make sure Shopify receives the selected variant ID.
       */
      const variantId = form.querySelector('#VariantId');

      if (variantId?.value) {
        formData.set('id', variantId.value);
      }

      /*
       * Make sure quantity is valid.
       */
      const quantityInput = form.querySelector('#Quantity');

      let quantity = parseInt(quantityInput?.value || '1', 10);

      if (!Number.isFinite(quantity) || quantity < 1) {
        quantity = 1;
      }

      formData.set('quantity', quantity);

      /*
       * AJAX Add to Cart.
       */
      const addResponse = await fetch(
        `${window.Shopify.routes.root}cart/add.js`,
        {
          method: 'POST',
          headers: {
            Accept: 'application/json'
          },
          body: formData
        }
      );

      const addResult = await addResponse.json();

      if (!addResponse.ok) {
        throw new Error(
          addResult?.description ||
          addResult?.message ||
          'Unable to add the product to the cart.'
        );
      }

      /*
       * Refresh the cart drawer using Shopify Section Rendering API.
       */
      await this.#refreshCartDrawer();

      /*
       * Open the existing theme drawer.
       */
      if (this.#themeDrawer && !this.#themeDrawer.isOpen) {
        this.#themeDrawer.open();
      }

      /*
       * Reset button state.
       */
      if (addButtonText) {
        addButtonText.textContent = 'Added to Cart';
      }

      /*
       * Return the button to normal state shortly afterwards.
       */
      window.setTimeout(() => {
        if (addButtonText) {
          addButtonText.textContent = originalButtonText;
        }

        if (addButton) {
          addButton.disabled = false;
        }

        form.dataset.cartSubmitting = 'false';
      }, 1200);

    } catch (error) {
      console.error('[cart-drawer] Add to cart failed:', error);

      if (addButtonText) {
        addButtonText.textContent = 'Try Again';
      }

      if (addButton) {
        addButton.disabled = false;
      }

      form.dataset.cartSubmitting = 'false';

      /*
       * Display the error without redirecting the customer.
       */
      this.#showCartError(
        error?.message || 'Unable to add this product to your cart.'
      );

      window.setTimeout(() => {
        if (addButtonText) {
          addButtonText.textContent = originalButtonText;
        }
      }, 2000);
    }
  };

  /**
   * Refresh the cart drawer using Shopify's Section Rendering API.
   */
  async #refreshCartDrawer() {
    const sectionUrl =
      `${window.location.pathname}?sections=cart-drawer-section`;

    const response = await fetch(sectionUrl, {
      method: 'GET',
      headers: {
        Accept: 'application/json'
      }
    });

    if (!response.ok) {
      throw new Error('Unable to refresh the cart drawer.');
    }

    const sections = await response.json();

    const sectionHtml = sections['cart-drawer-section'];

    if (!sectionHtml) {
      throw new Error('Cart drawer section was not returned by Shopify.');
    }

    /*
     * Parse the returned section HTML.
     */
    const parser = new DOMParser();

    const documentFragment = parser.parseFromString(
      sectionHtml,
      'text/html'
    );

    const newSection =
      documentFragment.querySelector('#shopify-section-cart-drawer-section');

    const currentSection =
      document.querySelector('#shopify-section-cart-drawer-section');

    /*
     * Replace the complete drawer section.
     */
    if (newSection && currentSection) {
      currentSection.replaceWith(newSection);
    } else if (newSection && !currentSection) {
      document.body.appendChild(newSection);
    }

    /*
     * The newly inserted cart-drawer-component will automatically
     * upgrade because its custom element is already registered.
     */
    await customElements.whenDefined('cart-drawer-component');

    /*
     * Give the browser one frame to finish replacing/upgrading
     * the drawer before opening it.
     */
    await new Promise((resolve) => requestAnimationFrame(resolve));
  }

  /**
   * Displays an Add to Cart error inside the product form.
   *
   * @param {string} message
   */
  #showCartError(message) {
    const form = document.getElementById('CustomProductForm');

    if (!form) return;

    let errorElement = form.querySelector('.cpp-cart-error');

    if (!errorElement) {
      errorElement = document.createElement('div');
      errorElement.className = 'cpp-cart-error';

      errorElement.style.marginTop = '12px';
      errorElement.style.padding = '10px 12px';
      errorElement.style.border = '1px solid #e74c3c';
      errorElement.style.background = '#fff5f5';
      errorElement.style.color = '#c0392b';
      errorElement.style.fontSize = '13px';

      form.appendChild(errorElement);
    }

    errorElement.textContent = message;
  }

  /**
   * Handles the theme drawer opening.
   */
  #handleDrawerOpen = () => {
    this.#updateStickyState();

    /*
     * Close cart drawer when installments CTA is clicked to avoid
     * overlapping dialogs.
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const cta =
        this.querySelector('shopify-payment-terms')
          ?.shadowRoot
          ?.querySelector('#shopify-installments-cta');

      cta?.addEventListener(
        'click',
        () => this.#themeDrawer?.close(),
        { once: true }
      );
    });
  };

  /**
   * Handles Shopify cart line update events.
   *
   * @param {import('@shopify/events').CartLinesUpdateEvent} event
   */
  #handleCartLinesUpdate = (event) => {
    const shouldAutoOpen =
      this.hasAttribute('auto-open') &&
      event.action === 'add' &&
      !this.#themeDrawer?.isOpen;

    /*
     * When the event originates inside an open modal dialog,
     * defer opening until that dialog closes.
     */
    const sourceModal =
      event.target instanceof Element
        ? event.target.closest('dialog:modal')
        : null;

    if (
      shouldAutoOpen &&
      !sourceModal &&
      !this.#isCartEmpty()
    ) {
      this.#themeDrawer?.open();
    }

    event.promise
      ?.then(({ detail }) => {
        const settle = () =>
          requestAnimationFrame(() => this.#updateStickyState());

        if (!shouldAutoOpen || detail?.didError) {
          settle();
          return;
        }

        const openAndSettle = () => {
          if (!this.#themeDrawer?.isOpen) {
            this.#themeDrawer?.open();
          }

          settle();
        };

        if (sourceModal?.open) {
          sourceModal.addEventListener(
            'close',
            openAndSettle,
            { once: true }
          );
        } else {
          openAndSettle();
        }
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') {
          console.warn(
            '[cart-drawer] Event promise rejected:',
            error
          );
        }
      });
  };

  /**
   * Check whether the cart is empty.
   */
  #isCartEmpty() {
    return Boolean(
      this.querySelector('.cart-drawer--empty')
    );
  }

  /**
   * Update sticky summary behavior.
   */
  #updateStickyState() {
    const dialog = this.#dialog;

    if (!dialog) return;

    const content =
      dialog.querySelector('.cart-drawer__content');

    const summary =
      dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      dialog.setAttribute(
        'cart-summary-sticky',
        'false'
      );

      return;
    }

    const drawerHeight =
      dialog.getBoundingClientRect().height;

    const summaryHeight =
      summary.getBoundingClientRect().height;

    const ratio =
      summaryHeight / drawerHeight;

    dialog.setAttribute(
      'cart-summary-sticky',
      ratio > this.#summaryThreshold
        ? 'false'
        : 'true'
    );
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define(
    'cart-drawer-component',
    CartDrawerComponent
  );
}